import { useTranslation } from 'react-i18next';
import { previewMenuHouses, type MenuHousePreviewItem } from '@/application/navigation/menuHouses';
import { Icon } from '@/presentation/components/common/Icon';
import { useMinWidthMd } from '@/presentation/hooks/useMinWidthMd';

/**
 * Preview del menú real (7 casas + Más) según las keys del rol.
 * Reusa `previewMenuHouses` / `canSeeMenuHouse` — no inventa casas.
 * Desktop: columna a la derecha. Mobile: chips compactos en el flujo (sin scroll propio).
 */
export function RoleMenuPreview({ permissionKeys }: { permissionKeys: readonly string[] }) {
  const { t } = useTranslation();
  const isDesktop = useMinWidthMd();
  const { primary, more } = previewMenuHouses(permissionKeys);
  const houses = [...primary, ...more];
  const anyVisible = houses.some((house) => house.visible);

  if (!isDesktop) {
    return <MobileMenuPreview primary={primary} more={more} anyVisible={anyVisible} />;
  }

  return (
    <aside
      data-testid="role-menu-preview"
      aria-label={t('permissions.modal.menuPreview')}
      className="order-1 shrink-0 border-b border-gray-200 bg-slate-50/80 px-4 py-3 dark:border-gray-700 dark:bg-slate-900/40 md:order-2 md:w-64 md:border-b-0 md:border-l md:py-4"
    >
      <h3 className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500 dark:text-slate-400">
        {t('permissions.modal.menuPreview')}
      </h3>
      {!anyVisible && (
        <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">{t('permissions.modal.menuPreviewEmpty')}</p>
      )}
      <ul className="mt-2 grid grid-cols-1 gap-x-2 gap-y-0.5">
        {primary.map((house) => (
          <PreviewRow key={house.id} house={house} />
        ))}
      </ul>
      <p className="mt-3 text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400 dark:text-slate-500">
        {t('menu.sections.more')}
      </p>
      <ul className="mt-1 grid grid-cols-1 gap-x-2 gap-y-0.5">
        {more.map((house) => (
          <PreviewRow key={house.id} house={house} />
        ))}
      </ul>
    </aside>
  );
}

function MobileMenuPreview({
  primary,
  more,
  anyVisible,
}: {
  primary: MenuHousePreviewItem[];
  more: MenuHousePreviewItem[];
  anyVisible: boolean;
}) {
  const { t } = useTranslation();
  const visiblePrimary = primary.filter((house) => house.visible);
  const visibleMore = more.filter((house) => house.visible);
  const hidden = [...primary, ...more].filter((house) => !house.visible);

  return (
    <aside
      data-testid="role-menu-preview"
      aria-label={t('permissions.modal.menuPreview')}
      className="rounded-xl border border-gray-200 bg-slate-50/90 p-3 dark:border-gray-700 dark:bg-slate-900/50"
    >
      <h3 className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500 dark:text-slate-400">
        {t('permissions.modal.menuPreview')}
      </h3>
      {!anyVisible ? (
        <MobileEmptyPreview houses={primary} />
      ) : (
        <>
          {visiblePrimary.length > 0 && (
            <ul className="mt-2 flex flex-wrap gap-1.5">
              {visiblePrimary.map((house) => (
                <PreviewChip key={house.id} house={house} />
              ))}
            </ul>
          )}
          {visibleMore.length > 0 && (
            <>
              <p className="mt-2 text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400 dark:text-slate-500">
                {t('menu.sections.more')}
              </p>
              <ul className="mt-1 flex flex-wrap gap-1.5">
                {visibleMore.map((house) => (
                  <PreviewChip key={house.id} house={house} />
                ))}
              </ul>
            </>
          )}
          {hidden.length > 0 && (
            <p className="mt-2 text-[11px] leading-snug text-slate-400 dark:text-slate-500">
              {t('permissions.modal.menuPreviewHiddenList', {
                names: hidden.map((house) => t(house.labelKey)).join(' · '),
              })}
            </p>
          )}
        </>
      )}
      <ul className="sr-only">
        {hidden.map((house) => (
          <li
            key={house.id}
            data-testid={`menu-preview-${house.id}`}
            data-visible="false"
            aria-label={`${t(house.labelKey)}: ${t('permissions.modal.menuPreviewHidden')}`}
          />
        ))}
      </ul>
    </aside>
  );
}

function MobileEmptyPreview({ houses }: { houses: MenuHousePreviewItem[] }) {
  const { t } = useTranslation();
  return (
    <div className="mt-2 rounded-xl border border-dashed border-slate-300/90 bg-white/70 px-3 py-4 dark:border-slate-600 dark:bg-slate-800/40">
      <ul className="flex flex-wrap justify-center gap-2" aria-hidden="true">
        {houses.map((house) => (
          <li
            key={house.id}
            className="flex h-9 w-9 items-center justify-center rounded-xl bg-slate-100 text-slate-400 dark:bg-slate-800 dark:text-slate-500"
          >
            <Icon path={house.iconPath} className="h-4 w-4" />
          </li>
        ))}
      </ul>
      <p className="mt-3 text-center text-xs leading-5 text-slate-500 dark:text-slate-400">
        {t('permissions.modal.menuPreviewEmpty')}
      </p>
    </div>
  );
}

function PreviewChip({ house }: { house: MenuHousePreviewItem }) {
  const { t } = useTranslation();
  const label = t(house.labelKey);
  return (
    <li
      data-testid={`menu-preview-${house.id}`}
      data-visible="true"
      aria-label={`${label}: ${t('permissions.modal.menuPreviewVisible')}`}
      className="inline-flex max-w-full items-center gap-1.5 rounded-full border border-teal-200/80 bg-white px-2.5 py-1 text-xs font-medium text-slate-800 dark:border-teal-900/50 dark:bg-slate-800 dark:text-slate-100"
    >
      <Icon path={house.iconPath} className="h-3.5 w-3.5 shrink-0 text-teal-700 dark:text-teal-300" />
      <span className="truncate">{label}</span>
    </li>
  );
}

function PreviewRow({ house }: { house: MenuHousePreviewItem }) {
  const { t } = useTranslation();
  const label = t(house.labelKey);
  const state = house.visible
    ? t('permissions.modal.menuPreviewVisible')
    : t('permissions.modal.menuPreviewHidden');
  return (
    <li
      data-testid={`menu-preview-${house.id}`}
      data-visible={house.visible ? 'true' : 'false'}
      aria-label={`${label}: ${state}`}
      className={`flex items-center gap-2 rounded-lg px-2 py-1.5 text-sm ${
        house.visible
          ? 'text-slate-800 dark:text-slate-100'
          : 'text-slate-400 line-through decoration-slate-300 dark:text-slate-500 dark:decoration-slate-600'
      }`}
    >
      <Icon path={house.iconPath} className="h-4 w-4 shrink-0" />
      <span className="min-w-0 truncate">{label}</span>
    </li>
  );
}
